import {createSlice} from '@reduxjs/toolkit';
import {Location, ReverseGeocode, UserLocation} from '../../api/types';
import {ForecastModelConfig} from '../../utils/Forecast/types';

type LocationState = {
  locationList: Location[];
  userLocation: UserLocation | null;
  selectedLocation: string | null;

  favoriteLocationHashs: string[];

  forecastModels: Record<string, ForecastModelConfig[]>;
  selectedForecastModel: Record<string, string>;
};

export const initialState: LocationState = {
  locationList: [],
  userLocation: null,
  selectedLocation: null,
  favoriteLocationHashs: [],
  forecastModels: {},
  selectedForecastModel: {},
};

export const weatherSlice = createSlice({
  name: 'location',
  initialState,
  reducers: {
    addLocation: (
      state,
      {payload: newLocation}: {payload: Location | null},
    ) => {
      state.locationList = state.locationList
        .filter((l) => l.hash !== newLocation?.hash)
        .concat([newLocation]);
    },
    addFavoriteLocation: (
      state,
      {payload: locationHash}: {payload: string},
    ) => {
      state.favoriteLocationHashs.push(locationHash);
    },
    removeFavoriteLocation: (
      state,
      {payload: locationHash}: {payload: string},
    ) => {
      state.favoriteLocationHashs = state.favoriteLocationHashs.filter(
        (hash) => hash !== locationHash,
      );
      state.locationList = state.locationList.filter(
        (l) => l.hash !== locationHash,
      );
    },
    reorderFavoriteLocations: (
      state,
      {payload: orderedHashes}: {payload: string[]},
    ) => {
      state.favoriteLocationHashs = orderedHashes;
    },
    cleanLocationList: (state) => {
      // Keep only favorite locations
      state.locationList = state.locationList.filter((l) =>
        state.favoriteLocationHashs.includes(l.hash),
      );
    },
    setUserLocation: (
      state,
      {payload: userLocation}: {payload: UserLocation | null},
    ) => {
      state.userLocation = userLocation;
    },

    /**
     * Merge a resolved place name into the GPS location.
     *
     * A merge rather than a `setUserLocation` replace, and guarded on the
     * coordinates, because reverse geocoding resolves asynchronously and the
     * location can change underneath it. Replacing wholesale with a snapshot
     * taken before the request would undo whatever happened in between —
     * including `setUserLocation(null)` when the user revokes the permission,
     * which would put them back on a location they just denied.
     */
    setUserLocationPlace: (
      state,
      {
        payload,
      }: {
        payload: {
          coordinates: {latitude: number; longitude: number};
          place: ReverseGeocode;
          placeLocale: string;
          title: string;
          subtitle?: string;
        };
      },
    ) => {
      const current = state.userLocation;
      if (
        !current ||
        current.coordinates.latitude !== payload.coordinates.latitude ||
        current.coordinates.longitude !== payload.coordinates.longitude
      ) {
        return;
      }
      current.place = payload.place;
      current.placeLocale = payload.placeLocale;
      current.title = payload.title;
      current.subtitle = payload.subtitle;
    },

    setSelectedLocation: (state, {payload: hash}: {payload: string}) => {
      state.selectedLocation = hash;
    },

    // Weather Forecast reducers
    setLocationForecastModels: (
      state,
      {
        payload: {hash, forecastModels},
      }: {
        payload: {
          hash: string;
          forecastModels: ForecastModelConfig[];
        };
      },
    ) => {
      const newForecastModels = {} as Record<number, ForecastModelConfig[]>;
      newForecastModels[hash] = forecastModels;
      state.forecastModels = {...state.forecastModels, ...newForecastModels};
    },
    setLocationSelectedForecastModel: (
      state,
      {
        payload: {locationHash, forecastModel},
      }: {payload: {locationHash: string; forecastModel: string}},
    ) => {
      const newSelectedForecastModel: Record<string, string> = {};
      newSelectedForecastModel[locationHash] = forecastModel;
      state.selectedForecastModel = {
        ...state.selectedForecastModel,
        ...newSelectedForecastModel,
      };
    },
    /**
     * Stop overriding this location. See the weather slice's twin for why this
     * deletes the key instead of blanking it.
     */
    clearLocationSelectedForecastModel: (
      state,
      {payload: {locationHash}}: {payload: {locationHash: string}},
    ) => {
      delete state.selectedForecastModel[locationHash];
    },
  },
});

export const {
  setUserLocation,
  setUserLocationPlace,
  setSelectedLocation,
  setLocationForecastModels,
  setLocationSelectedForecastModel,
  clearLocationSelectedForecastModel,
  addLocation,
  addFavoriteLocation,
  removeFavoriteLocation,
  reorderFavoriteLocations,
  cleanLocationList,
} = weatherSlice.actions;

export default weatherSlice.reducer;
